// client/src/pages/AdminAnalytics.js
import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import { Box, Typography, Grid, Card, CardContent, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody } from '@mui/material';
import { formatCurrency } from '../utils/currency';
import MiniBarChart from '../components/MiniBarChart';
import { API_BASE_URL } from '../utils/media';

const StatCard = ({ label, value }) => (
  <Card variant="outlined">
    <CardContent>
      <Typography variant="body2" color="text.secondary">{label}</Typography>
      <Typography variant="h4" fontWeight={700}>{value}</Typography>
    </CardContent>
  </Card>
);

const AdminAnalytics = () => {
  const { token } = useContext(AuthContext);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!token) return;
    fetch(`${API_BASE_URL}/api/admin/analytics`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : null))
      .then(setData)
      .finally(() => setLoading(false));
  }, [token]);

  if (loading) return <CircularProgress size={28} />;
  if (!data) return <Typography color="text.secondary">Could not load analytics.</Typography>;

  return (
    <Box>
      <Typography variant="h5" fontWeight={700} gutterBottom>Platform Analytics</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={4}><StatCard label="Total Revenue (paid orders)" value={formatCurrency(data.revenue)} /></Grid>
        <Grid item xs={6} sm={4}><StatCard label="Paid Orders" value={data.orderCount} /></Grid>
        <Grid item xs={6} sm={4}><StatCard label="Total Users" value={data.userCount} /></Grid>
      </Grid>

      <Grid container spacing={4}>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>Revenue — Last 30 Days</Typography>
          <MiniBarChart data={data.revenueByDay} labelKey="day" valueKey="revenue" formatValue={formatCurrency} />
        </Grid>
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" fontWeight={700} gutterBottom>New Users — Last 30 Days</Typography>
          <MiniBarChart data={data.userGrowth} labelKey="day" valueKey="newUsers" />
        </Grid>
      </Grid>

      <Typography variant="subtitle1" fontWeight={700} sx={{ mt: 3 }} gutterBottom>Top Products (platform-wide)</Typography>
      <Table size="small">
        <TableHead>
          <TableRow><TableCell>Product</TableCell><TableCell align="right">Units Sold</TableCell><TableCell align="right">Revenue</TableCell></TableRow>
        </TableHead>
        <TableBody>
          {data.topProducts.map((p) => (
            <TableRow key={p.id}>
              <TableCell>{p.name}</TableCell>
              <TableCell align="right">{p.unitsSold}</TableCell>
              <TableCell align="right">{formatCurrency(p.revenue)}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
};

export default AdminAnalytics;
